/**
 * Frescura de los datos: cuándo escribió dbt la tabla que alimenta la consola (BB-1627).
 *
 * POR QUÉ ESTO Y NO "UNA HORA SEGURA PARA OPS". BB-1551 midió que de los tres momentos de la cadena
 * solo uno tiene hora de verdad —el build de dbt, cron 05:30 UTC—; el pago de Backend y la apertura
 * de Ops son manuales. Así que no hay ninguna hora que decirle a Ops que sea cierta siempre. Se
 * cambia "memoriza una hora" por "mira un dato en pantalla antes de fiarte de las cifras".
 *
 * QUÉ PRUEBA ESTE DATO, Y QUÉ NO. `last_modified_time` dice que dbt ESCRIBIÓ la tabla, no que los
 * viajes de hoy estén dentro: un rebuild sin filas nuevas también lo mueve. Es un proxy suficiente,
 * y por eso el texto NUNCA dice "datos de hoy" — dice cuándo se escribió la tabla, que es lo único
 * que sabemos.
 *
 * EL UMBRAL ES TIEMPO TRANSCURRIDO, no "¿el último write es posterior al 05:30 UTC de hoy?", que
 * sería más preciso. Motivo: esa segunda forma mete el 05:30 hardcodeado en el front, y el
 * `CLAUDE.md` dice que ninguna hora se hardcodea porque el corte es configuración. El tiempo
 * transcurrido no lleva ninguna hora dentro, y cubre el caso: un build fallido se ve como ≥24 h a
 * cualquier hora del día.
 *
 * `ahoraMs` se inyecta en vez de leer el reloj aquí dentro, para que sea testeable.
 */

/** Más de esto y la frescura pasa de dato a aviso. Un build fallido cae siempre por encima. */
export const HORAS_PARA_AVISO = 20;

export interface Frescura {
  /** Línea principal, la que se lee. */
  texto: string;
  /** `true` cuando hay que pintarlo como aviso y no como dato neutro. */
  esAviso: boolean;
  /**
   * Solo cuando `esAviso`: qué hacer al respecto. Un aviso sin acción es ruido, y en una pantalla de
   * dinero el ruido se aprende a ignorar.
   */
  queHacer: string | null;
}

const RELOJ_MADRID = new Intl.DateTimeFormat('es-ES', {
  timeZone: 'Europe/Madrid',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * El caso de "no lo sabemos" degrada a AVISO, no a silencio.
 *
 * Silenciar un dato que falta lo hace indistinguible de un dato bueno, y aquí ese dato es lo que
 * decide si Ops se fía de las cifras. Falla hacia la duda visible.
 */
const SIN_DATO: Frescura = {
  texto: 'No se puede determinar cuándo se actualizaron los datos',
  esAviso: true,
  queHacer: 'Comprueba con Data antes de confirmar nada a partir de estas cifras.',
};

/**
 * @param iso Lo que devuelve `/preview` en `datosActualizadosEn`.
 *   ⚠️ Se acepta `undefined` además de `null` A PROPÓSITO: el front NO valida la respuesta con Zod
 *   (`httpsCallable` es solo una aserción de tipos), así que contra un backend desplegado que todavía
 *   no manda el campo llega `undefined` mientras TypeScript cree que es `string | null`. Sin este
 *   caso, un `new Date(undefined)` pintaría `NaN` en pantalla y nadie sabría por qué.
 */
export function describirFrescura(
  iso: string | null | undefined,
  ahoraMs: number,
): Frescura {
  if (iso === null || iso === undefined || iso === '') return SIN_DATO;

  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return SIN_DATO;

  const cuando = RELOJ_MADRID.format(new Date(ms));
  // Un reloj de cliente adelantado puede dar transcurrido negativo. Se trata como recién escrito en
  // vez de pintar "hace -2 h", que parece un bug y distrae del dato.
  const transcurridoMs = Math.max(0, ahoraMs - ms);
  const horas = transcurridoMs / 3_600_000;

  const hace = describirTranscurrido(transcurridoMs);
  const texto = `Datos actualizados ${hace} (${cuando})`;

  if (horas < HORAS_PARA_AVISO) {
    return { texto, esAviso: false, queHacer: null };
  }

  return {
    texto,
    esAviso: true,
    // No se dice "el build ha fallado": no lo sabemos. Se dice qué implica y qué hacer.
    queHacer:
      'Es probable que el último build de datos no haya llegado. Las cifras pueden ser de ayer: ' +
      'compruébalo con Data antes de confirmar.',
  };
}

function describirTranscurrido(ms: number): string {
  const minutos = Math.floor(ms / 60_000);
  if (minutos < 1) return 'ahora mismo';
  if (minutos < 60) return `hace ${minutos} min`;

  const horas = Math.floor(ms / 3_600_000);
  if (horas < 48) return `hace ${horas} h`;

  return `hace ${Math.floor(horas / 24)} días`;
}
